import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GridColDef } from '@mui/x-data-grid';
import { Button, Box, Typography, Alert, Snackbar, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { DataTable } from '../components/DataTable';
import { FormDialog } from '../components/FormDialog';
import { manufacturersApi } from '../api/services';
import { extractApiError } from '../utils/apiErrors';
import type { Manufacturer } from '../api/config';

export const ManufacturersPage: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [selectedManufacturer, setSelectedManufacturer] = React.useState<Manufacturer | undefined>();
  const [formName, setFormName] = React.useState('');
  const [snackbar, setSnackbar] = React.useState<{ message: string; severity: 'error' | 'success' } | null>(null);

  const queryClient = useQueryClient();

  const { data: manufacturers = [], isLoading, error } = useQuery({
    queryKey: ['manufacturers'],
    queryFn: manufacturersApi.getAll,
  });

  const createMutation = useMutation({
    mutationFn: manufacturersApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manufacturers'] });
      setSnackbar({ message: 'Manufacturer created successfully', severity: 'success' });
      handleCloseForm();
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to create manufacturer'), severity: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<{ name: string }> }) =>
      manufacturersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manufacturers'] });
      setSnackbar({ message: 'Manufacturer updated successfully', severity: 'success' });
      handleCloseForm();
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to update manufacturer'), severity: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: manufacturersApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manufacturers'] });
      setSnackbar({ message: 'Manufacturer deleted successfully', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to delete manufacturer'), severity: 'error' });
    },
  });

  const mutationError = createMutation.error || updateMutation.error;

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'name', headerName: 'Name', width: 300, resizable: true },
  ];

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedManufacturer(undefined);
    setFormName('');
    createMutation.reset();
    updateMutation.reset();
  };

  const handleOpenCreate = () => {
    setSelectedManufacturer(undefined);
    setFormName('');
    setIsFormOpen(true);
  };

  const handleEdit = (id: string) => {
    const manufacturer = manufacturers.find((m) => m.id.toString() === id);
    if (manufacturer) {
      setSelectedManufacturer(manufacturer);
      setFormName(manufacturer.name);
      setIsFormOpen(true);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this manufacturer?')) {
      deleteMutation.mutate(Number(id));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) return;
    createMutation.reset();
    updateMutation.reset();
    if (selectedManufacturer) {
      updateMutation.mutate({ id: selectedManufacturer.id, data: { name: formName.trim() } });
    } else {
      createMutation.mutate({ name: formName.trim() });
    }
  };

  if (error) {
    return <Alert severity="error">Error loading manufacturers</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" component="h1">
          Manufacturers
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          Add Manufacturer
        </Button>
      </Box>

      <DataTable
        rows={manufacturers}
        columns={columns}
        loading={isLoading}
        onEditClick={handleEdit}
        onDeleteClick={handleDelete}
        getRowId={(row: Manufacturer) => row.id.toString()}
        fullHeight
      />

      <FormDialog
        open={isFormOpen}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
        title={selectedManufacturer ? 'Edit Manufacturer' : 'Create Manufacturer'}
        submitLabel={selectedManufacturer ? 'Update' : 'Create'}
        error={mutationError ? extractApiError(mutationError) : undefined}
        isPending={createMutation.isPending || updateMutation.isPending}
        submitDisabled={!formName.trim()}
      >
        <TextField
          autoFocus
          label="Name"
          value={formName}
          onChange={(e) => setFormName(e.target.value)}
          fullWidth
          required
          sx={{ mt: 1 }}
        />
      </FormDialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
      >
        <Box>
          {snackbar && (
            <Alert
              severity={snackbar.severity}
              onClose={() => setSnackbar(null)}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          )}
        </Box>
      </Snackbar>
    </Box>
  );
};
